// Today-page math (Product_Roadmap: the Clock page becomes time-aware —
// briefing before the session, score after it) and per-symbol memory
// (Memory Engine v2). Pure and unit-tested; UI lives in the pages.

import type { TradeEntry } from "./journal-store.js";
import { etComponents } from "./session-math.js";

export interface TodayStats {
  trades: number;
  totalR: number;
  best: TradeEntry | null;
  worst: TradeEntry | null;
  /** Today's last written lesson — tomorrow's reminder. */
  lastLesson: string | null;
}

/** Entries logged on the same LOCAL calendar day as `now`. Pure. */
export function entriesForDay(entries: TradeEntry[], now: Date): TradeEntry[] {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const end = start + 86400_000;
  return entries.filter((e) => e.date >= start && e.date < end);
}

export function todayStats(entries: TradeEntry[], now: Date): TodayStats {
  const today = entriesForDay(entries, now);
  const byR = [...today].sort((a, b) => b.rMultiple - a.rMultiple);
  const lessons = today
    .slice()
    .sort((a, b) => b.date - a.date)
    .map((e) => e.lesson.trim())
    .filter(Boolean);
  return {
    trades: today.length,
    totalR: today.reduce((s, e) => s + e.rMultiple, 0),
    best: byR[0] ?? null,
    worst: byR.length > 1 ? byR[byR.length - 1]! : null,
    lastLesson: lessons[0] ?? null,
  };
}

/** After the NYSE close (16:00 ET) the day is scoreable. Pure. */
export function isAfterClose(now: Date): boolean {
  const { hour } = etComponents(now);
  return hour >= 16 || hour < 4; // evening ET, incl. late-night review
}

export interface SymbolMemory {
  trades: number;
  totalR: number;
  winRate: number; // 0–100
  lastLesson: string | null;
}

/** The Memory Engine's per-symbol recall. Null below 2 trades — the same
 * silence contract used throughout: no claims from thin data. */
export function symbolMemory(entries: TradeEntry[], symbol: string): SymbolMemory | null {
  const s = symbol.trim().toUpperCase();
  const trades = entries.filter((e) => e.symbol.trim().toUpperCase() === s);
  if (trades.length < 2) return null;
  const lessons = trades
    .slice()
    .sort((a, b) => b.date - a.date)
    .map((e) => e.lesson.trim())
    .filter(Boolean);
  return {
    trades: trades.length,
    totalR: trades.reduce((sum, e) => sum + e.rMultiple, 0),
    winRate: (trades.filter((e) => e.rMultiple > 0).length / trades.length) * 100,
    lastLesson: lessons[0] ?? null,
  };
}

/** Per-setup recall for the journal form. Same 2-trade minimum. */
export function setupMemory(entries: TradeEntry[], tag: string): SymbolMemory | null {
  const trades = entries.filter((e) => e.setupTag === tag);
  if (trades.length < 2) return null;
  const lessons = trades
    .slice()
    .sort((a, b) => b.date - a.date)
    .map((e) => e.lesson.trim())
    .filter(Boolean);
  return {
    trades: trades.length,
    totalR: trades.reduce((sum, e) => sum + e.rMultiple, 0),
    winRate: (trades.filter((e) => e.rMultiple > 0).length / trades.length) * 100,
    lastLesson: lessons[0] ?? null,
  };
}
